import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Chip,
} from "@mui/material";
import { Save, Add, Edit, Delete } from "@mui/icons-material";
import type { Timestamp } from "firebase/firestore";
import {
  DEFAULT_CONFIG,
  deleteLocation as deleteConfigLocation,
  normalizeNumber,
  saveAppConfig,
  saveLocation as saveConfigLocation,
  subscribeToAppConfig,
  subscribeToLocations,
  type ConfigFormState,
  type LocationItem,
  type LocationKind,
} from "../services/configService";

type EditDialogState = {
  open: boolean;
  kind: LocationKind;
  mode: "add" | "edit";
  item: LocationItem | null;
};

const EMPTY_DIALOG_STATE: EditDialogState = {
  open: false,
  kind: "diningHalls",
  mode: "add",
  item: null,
};

export default function Config() {
  const [config, setConfig] = useState<ConfigFormState>(DEFAULT_CONFIG);
  const [initialConfig, setInitialConfig] = useState<ConfigFormState | null>(null);
  const [configCreatedAt, setConfigCreatedAt] = useState<Timestamp | null>(null);

  const [diningHalls, setDiningHalls] = useState<LocationItem[]>([]);
  const [dorms, setDorms] = useState<LocationItem[]>([]);

  const [configReady, setConfigReady] = useState(false);
  const [diningReady, setDiningReady] = useState(false);
  const [dormsReady, setDormsReady] = useState(false);

  const [saveLoading, setSaveLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const [editDialog, setEditDialog] = useState<EditDialogState>(EMPTY_DIALOG_STATE);
  const [dialogName, setDialogName] = useState("");
  const [dialogActive, setDialogActive] = useState(true);

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    return subscribeToAppConfig(
      (nextConfig, createdAt) => {
        setConfig(nextConfig);
        setInitialConfig(nextConfig);
        setConfigCreatedAt(createdAt);
        setConfigReady(true);
      },
      (message) => {
        setConfigReady(true);
        setError(message);
      },
    );
  }, []);

  useEffect(() => {
    return subscribeToLocations(
      "diningHalls",
      (locations) => {
        setDiningHalls(locations);
        setDiningReady(true);
      },
      (message) => {
        setDiningReady(true);
        setError(message);
      },
    );
  }, []);

  useEffect(() => {
    return subscribeToLocations(
      "dorms",
      (locations) => {
        setDorms(locations);
        setDormsReady(true);
      },
      (message) => {
        setDormsReady(true);
        setError(message);
      },
    );
  }, []);

  const loading = !(configReady && diningReady && dormsReady);

  const pricingEffectiveLabel = useMemo(() => {
    if (!config.pricingEffectiveAt?.toDate) {
      return "Not set";
    }

    return config.pricingEffectiveAt.toDate().toLocaleString();
  }, [config.pricingEffectiveAt]);

  const locationListMap = useMemo(() => {
    return {
      diningHalls,
      dorms,
    };
  }, [diningHalls, dorms]);

  const handleSave = async () => {
    setSaveLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await saveAppConfig(config, initialConfig, configCreatedAt);
      setSuccess("Configuration saved.");
    } catch (saveError) {
      setError(
        saveError instanceof Error
          ? saveError.message
          : "Failed to save configuration.",
      );
    } finally {
      setSaveLoading(false);
    }
  };

  const openAddDialog = (kind: LocationKind) => {
    setDialogName("");
    setDialogActive(true);
    setEditDialog({
      open: true,
      kind,
      mode: "add",
      item: null,
    });
  };

  const openEditDialog = (kind: LocationKind, item: LocationItem) => {
    setDialogName(item.name);
    setDialogActive(item.active);
    setEditDialog({
      open: true,
      kind,
      mode: "edit",
      item,
    });
  };

  const closeDialog = () => {
    if (actionLoading) {
      return;
    }
    setEditDialog(EMPTY_DIALOG_STATE);
  };

  const handleSaveLocation = async () => {
    const name = dialogName.trim();
    if (!name) {
      setError("Location name is required.");
      return;
    }

    setActionLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await saveConfigLocation(
        editDialog.kind,
        editDialog.mode,
        editDialog.item?.id || null,
        name,
        dialogActive,
      );
      setSuccess("Location updated.");
      setEditDialog(EMPTY_DIALOG_STATE);
    } catch (locationError) {
      setError(
        locationError instanceof Error
          ? locationError.message
          : "Failed to save location.",
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteLocation = async (kind: LocationKind, item: LocationItem) => {
    setActionLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await deleteConfigLocation(kind, item.id);
      setSuccess(`${item.name} deleted.`);
    } catch (deleteError) {
      setError(
        deleteError instanceof Error
          ? deleteError.message
          : "Failed to delete location.",
      );
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4">Configuration</Typography>
        <Button
          variant="contained"
          startIcon={<Save />}
          onClick={handleSave}
          disabled={saveLoading || loading}
        >
          {saveLoading ? "Saving..." : "Save Changes"}
        </Button>
      </Box>

      {error && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {success && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="success">{success}</Alert>
        </Box>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: { xs: "flex-start", sm: "center" },
                    gap: 2,
                    flexDirection: { xs: "column", sm: "row" },
                  }}
                >
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                      <Typography variant="h6">Delivery Launch Control</Typography>
                      <Chip
                        size="small"
                        color={config.orderingEnabled ? "success" : "warning"}
                        label={config.orderingEnabled ? "Open" : "Paused"}
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      This controls whether the live student app accepts new
                      delivery orders. The App Store listing can stay live while
                      deliveries are paused.
                    </Typography>
                  </Box>
                  <FormControlLabel
                    sx={{ m: 0 }}
                    control={
                      <Switch
                        checked={config.orderingEnabled}
                        onChange={(event) =>
                          setConfig((prev) => ({
                            ...prev,
                            orderingEnabled: event.target.checked,
                          }))
                        }
                        inputProps={{
                          "aria-label": "Accepting Delivery Orders",
                        }}
                      />
                    }
                    label="Accepting Delivery Orders"
                  />
                </Box>
                <Alert
                  severity={config.orderingEnabled ? "success" : "warning"}
                  sx={{ mt: 2 }}
                >
                  {config.orderingEnabled
                    ? "Ordering is open. Students can place wallet orders, and the backend will accept them."
                    : "Ordering is paused. Students see ordering as unavailable, and the backend blocks new wallet orders."}
                </Alert>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Change this switch, then click Save Changes.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: { xs: "flex-start", md: "center" },
                    gap: 2,
                    flexDirection: { xs: "column", md: "row" },
                    mb: 2,
                  }}
                >
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                      <Typography variant="h6">Pilot Mode</Typography>
                      <Chip
                        size="small"
                        color={config.pilotModeEnabled ? "info" : "default"}
                        label={config.pilotModeEnabled ? "Invite controlled" : "Standard"}
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Use this while UCSD approval and rider operations are pending. These
                      values guide launch-night operations and the student wait-state copy.
                    </Typography>
                  </Box>
                  <FormControlLabel
                    sx={{ m: 0 }}
                    control={
                      <Switch
                        checked={config.pilotModeEnabled}
                        onChange={(event) =>
                          setConfig((prev) => ({
                            ...prev,
                            pilotModeEnabled: event.target.checked,
                          }))
                        }
                        inputProps={{ "aria-label": "Pilot Mode Enabled" }}
                      />
                    }
                    label="Pilot Mode Enabled"
                  />
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Pilot cohort label"
                      value={config.pilotCohortLabel}
                      onChange={(event) =>
                        setConfig((prev) => ({
                          ...prev,
                          pilotCohortLabel: event.target.value,
                        }))
                      }
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      label="Nightly cap"
                      type="number"
                      value={config.launchNightOrderCap}
                      onChange={(event) =>
                        setConfig((prev) => ({
                          ...prev,
                          launchNightOrderCap: normalizeNumber(
                            event.target.value,
                            prev.launchNightOrderCap,
                          ),
                        }))
                      }
                      fullWidth
                      inputProps={{ min: 0, step: 1 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      label="Min riders"
                      type="number"
                      value={config.minimumOnlineRiders}
                      onChange={(event) =>
                        setConfig((prev) => ({
                          ...prev,
                          minimumOnlineRiders: normalizeNumber(
                            event.target.value,
                            prev.minimumOnlineRiders,
                          ),
                        }))
                      }
                      fullWidth
                      inputProps={{ min: 0, step: 1 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      label="Max searching"
                      type="number"
                      value={config.maxSearchingOrders}
                      onChange={(event) =>
                        setConfig((prev) => ({
                          ...prev,
                          maxSearchingOrders: normalizeNumber(
                            event.target.value,
                            prev.maxSearchingOrders,
                          ),
                        }))
                      }
                      fullWidth
                      inputProps={{ min: 0, step: 1 }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Student launch message"
                      value={config.pilotAnnouncement}
                      onChange={(event) =>
                        setConfig((prev) => ({
                          ...prev,
                          pilotAnnouncement: event.target.value,
                        }))
                      }
                      fullWidth
                      multiline
                      minRows={2}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Pricing Settings
                </Typography>

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel id="pricing-model-label">Pricing Model</InputLabel>
                  <Select
                    labelId="pricing-model-label"
                    value={config.pricingModel}
                    label="Pricing Model"
                    onChange={(event) =>
                      setConfig((prev) => ({
                        ...prev,
                        pricingModel: event.target.value as ConfigFormState["pricingModel"],
                      }))
                    }
                  >
                    <MenuItem value="flat">Flat fee</MenuItem>
                    <MenuItem value="distance">Base fee + distance</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  label={
                    config.pricingModel === "distance"
                      ? "Base Fee ($)"
                      : "Delivery Fee ($)"
                  }
                  type="number"
                  value={config.deliveryFee}
                  onChange={(event) =>
                    setConfig((prev) => ({
                      ...prev,
                      deliveryFee: normalizeNumber(event.target.value, prev.deliveryFee),
                    }))
                  }
                  fullWidth
                  sx={{ mb: 2 }}
                  inputProps={{ step: 0.01, min: 0 }}
                />

                {config.pricingModel === "distance" && (
                  <TextField
                    label="Distance Fee Per Mile ($)"
                    type="number"
                    value={config.distanceFeePerMile}
                    onChange={(event) =>
                      setConfig((prev) => ({
                        ...prev,
                        distanceFeePerMile: normalizeNumber(
                          event.target.value,
                          prev.distanceFeePerMile,
                        ),
                      }))
                    }
                    helperText="Uses straight-line campus distance between the dining hall and dorm."
                    fullWidth
                    sx={{ mb: 2 }}
                    inputProps={{ step: 0.01, min: 0 }}
                  />
                )}

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Tip Presets ($)
                </Typography>
                <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                  {config.tipPresets.map((tip, index) => (
                    <TextField
                      key={`tip-${index}`}
                      type="number"
                      value={tip}
                      onChange={(event) => {
                        const nextValue = normalizeNumber(event.target.value, tip);
                        setConfig((prev) => {
                          const nextPresets = [...prev.tipPresets];
                          nextPresets[index] = nextValue;
                          return { ...prev, tipPresets: nextPresets };
                        });
                      }}
                      size="small"
                      inputProps={{ step: 0.5, min: 0 }}
                    />
                  ))}
                </Box>

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Wallet Top-up Presets ($)
                </Typography>
                <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                  {config.walletTopUpPresets.map((amount, index) => (
                    <TextField
                      key={`wallet-topup-${index}`}
                      type="number"
                      value={amount}
                      onChange={(event) => {
                        const nextValue = normalizeNumber(event.target.value, amount);
                        setConfig((prev) => {
                          const nextPresets = [...prev.walletTopUpPresets];
                          nextPresets[index] = nextValue;
                          return { ...prev, walletTopUpPresets: nextPresets };
                        });
                      }}
                      size="small"
                      inputProps={{ step: 1, min: 0 }}
                    />
                  ))}
                </Box>

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Wallet Bonus Tiers
                </Typography>
                <Box sx={{ display: "grid", gap: 1.5, mb: 2 }}>
                  {config.walletTopUpBonuses.map((bonus, index) => (
                    <Box
                      key={`wallet-bonus-${index}`}
                      sx={{ display: "flex", gap: 1, alignItems: "center" }}
                    >
                      <TextField
                        label="Top-up ($)"
                        type="number"
                        value={bonus.topUpAmount}
                        onChange={(event) => {
                          const nextValue = normalizeNumber(
                            event.target.value,
                            bonus.topUpAmount,
                          );
                          setConfig((prev) => {
                            const nextBonuses = [...prev.walletTopUpBonuses];
                            nextBonuses[index] = {
                              ...nextBonuses[index],
                              topUpAmount: nextValue,
                            };
                            return { ...prev, walletTopUpBonuses: nextBonuses };
                          });
                        }}
                        size="small"
                        inputProps={{ step: 1, min: 0 }}
                      />
                      <TextField
                        label="Bonus ($)"
                        type="number"
                        value={bonus.bonusAmount}
                        onChange={(event) => {
                          const nextValue = normalizeNumber(
                            event.target.value,
                            bonus.bonusAmount,
                          );
                          setConfig((prev) => {
                            const nextBonuses = [...prev.walletTopUpBonuses];
                            nextBonuses[index] = {
                              ...nextBonuses[index],
                              bonusAmount: nextValue,
                            };
                            return { ...prev, walletTopUpBonuses: nextBonuses };
                          });
                        }}
                        size="small"
                        inputProps={{ step: 0.5, min: 0 }}
                      />
                    </Box>
                  ))}
                </Box>

                <Typography variant="body2" color="text.secondary">
                  Pricing version: {config.pricingVersion}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Effective at: {pricingEffectiveLabel}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Service Settings
                </Typography>

                <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                  <TextField
                    label="Start Time"
                    type="time"
                    value={config.serviceHours.start}
                    onChange={(event) =>
                      setConfig((prev) => ({
                        ...prev,
                        serviceHours: {
                          ...prev.serviceHours,
                          start: event.target.value,
                        },
                      }))
                    }
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ step: 300 }}
                  />
                  <TextField
                    label="End Time"
                    type="time"
                    value={config.serviceHours.end}
                    onChange={(event) =>
                      setConfig((prev) => ({
                        ...prev,
                        serviceHours: {
                          ...prev.serviceHours,
                          end: event.target.value,
                        },
                      }))
                    }
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ step: 300 }}
                  />
                </Box>

                <TextField
                  label="Max Search Radius (miles)"
                  type="number"
                  value={config.maxSearchRadius}
                  onChange={(event) =>
                    setConfig((prev) => ({
                      ...prev,
                      maxSearchRadius: normalizeNumber(event.target.value, prev.maxSearchRadius),
                    }))
                  }
                  fullWidth
                  sx={{ mb: 2 }}
                  inputProps={{ min: 1, max: 10 }}
                />

                <TextField
                  label="Rider Timeout (seconds)"
                  type="number"
                  value={config.riderTimeout}
                  onChange={(event) =>
                    setConfig((prev) => ({
                      ...prev,
                      riderTimeout: normalizeNumber(event.target.value, prev.riderTimeout),
                    }))
                  }
                  fullWidth
                  helperText="How long to wait before cancelling search"
                  inputProps={{ min: 30, max: 600 }}
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="h6">Dining Halls</Typography>
                  <Button size="small" startIcon={<Add />} onClick={() => openAddDialog("diningHalls")}>Add</Button>
                </Box>

                <List>
                  {locationListMap.diningHalls.map((hall) => (
                    <ListItem key={hall.id} divider>
                      <ListItemText
                        primary={hall.name}
                        secondary={hall.active ? "Active" : "Inactive"}
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          size="small"
                          aria-label={`Edit ${hall.name}`}
                          onClick={() => openEditDialog("diningHalls", hall)}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          aria-label={`Delete ${hall.name}`}
                          onClick={() => handleDeleteLocation("diningHalls", hall)}
                          disabled={actionLoading}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="h6">Dorms</Typography>
                  <Button size="small" startIcon={<Add />} onClick={() => openAddDialog("dorms")}>Add</Button>
                </Box>

                <List>
                  {locationListMap.dorms.map((dorm) => (
                    <ListItem key={dorm.id} divider>
                      <ListItemText
                        primary={dorm.name}
                        secondary={dorm.active ? "Active" : "Inactive"}
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          size="small"
                          aria-label={`Edit ${dorm.name}`}
                          onClick={() => openEditDialog("dorms", dorm)}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          aria-label={`Delete ${dorm.name}`}
                          onClick={() => handleDeleteLocation("dorms", dorm)}
                          disabled={actionLoading}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Dialog open={editDialog.open} onClose={closeDialog}>
        <DialogTitle>
          {editDialog.mode === "edit" ? "Edit Item" : "Add New Item"}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            value={dialogName}
            onChange={(event) => setDialogName(event.target.value)}
            fullWidth
            sx={{ mb: 2, mt: 1 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={dialogActive}
                onChange={(event) => setDialogActive(event.target.checked)}
              />
            }
            label="Active"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={actionLoading}>
            Cancel
          </Button>
          <Button onClick={handleSaveLocation} variant="contained" disabled={actionLoading}>
            {actionLoading
              ? "Saving..."
              : editDialog.mode === "edit"
                ? "Update"
                : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
